import moment from "moment";
import { DASHED, EMPTY_STRING, GET_ALL_PRODUCT_URL, GET_CERT_URL, GET_COL_COMP_URL, GET_COLOR_URL, GET_COUNT_URL, GET_MAT_COMP_URL, GET_QUALITY_URL, TYPE_LABOR, TYPE_MATERIAL, TYPE_PROCESS } from "./constants";
import { LABOR_SPECS_MAP, MATERIAL_SPECS_MAP, PROCESS_SPECS_MAP } from "../master-data/specs/specs.mapping";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

export function isNull(value: any) {
    return value === undefined || value === null;
}

export function isNotEmptyArray(value: any) {
    return !isNull(value) && value.length > 0;
}

export function isEmptyArray(value: any) {
    return !isNotEmptyArray(value);
}

export function isNullCheck(value: any) {
    return value === undefined || value === null || value === 0 || value === EMPTY_STRING;
}

export function isZero(value: any) {
    return value === undefined || value === null || value === 0 || value === EMPTY_STRING || value === '0';
}

export function isNullData(data: any) {
    return data === undefined || data === null || data === EMPTY_STRING;
}

export function isNotNull(value: any) {
    return !isNull(value);
}

export function isEmpty(value: any) {
    return isNull(value) || value === EMPTY_STRING;
}

export function isNotEmpty(value: any) {
    return !isEmpty(value);
}

export function formatTo2Decimal(value: any) {
    if(isEmpty(value)) {
        return 0;
    }
    return value.toFixed(2);
}

export function formatTo1Decimal(value: any) {
    if(isEmpty(value)) {
        return 0;
    }
    return value.toFixed(1);
}

export function formatToNumber(value: any) {
    if(isEmpty(value)) {
        return 0;
    }
    return (Math.round(value * 100)/100).toLocaleString('en-IN');
}

export function formatToNumberRound(value: any) {
    if(isEmpty(value)) {
        return 0;
    }
    return Math.round(value).toLocaleString('en-IN');
}

export function formatTo0Decimal(value: any) {
    return isEmpty(value) ? 0 : (Math.round(value) + 0).toLocaleString('en-IN');
}

export function formatDate(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MMM-YY')
}

export function formatDateSmall(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MMM')
}

export function convertToStartDate(dateString: string) {
    dayjs.extend(customParseFormat);
    const date = dayjs(dateString, "MMM-YY").startOf('month');
    return date.format("YYYY-MM-DD");
}

export function convertToEndDate(dateString: string) {
    dayjs.extend(customParseFormat);
    const date = dayjs(dateString, "MMM-YY").endOf('month');
    return date.format("YYYY-MM-DD");
}

export function formatDateTimeSmall(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MMM, hh:mm A')
}

export function formatDateTrn(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('YYYY-MM-DD')
}

export function formatDateLong(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MMM-YY, hh:mm:ss A')
}

export function formatDateOrder(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MM-YY, hh:mm:ss A')
}

export function formatDateDay(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('DD-MMM-YY, dddd')
}

export function formatTime(value: any) {
    if(isEmpty(value)) {
        return DASHED;
    }
    return moment(value).format('hh:mm:ss A')
}

export const numberFormat = (value: any) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: '₹'
    }).format(value);

export const showDataDash = (value: any) => {
    if (value !== null && value !== undefined && value !== '' && value !== ' ' && value !== 'Invalid date') {
        return value;
    }
    return '-';
}

export function shuffleArray(array: any) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
}

export function removeSpaces(array: any) {
    let temp = [];
    for (let i = 0; i < array.length; i++) {
        if (array[i] !== '' && array[i] !== ' ') {
            temp.push(array[i]);
        }
    }
    return temp;
}

export function generateId() {
    return moment(Date.now()).format('DDMMYYYYHHmmssSSS')
}

export function isPacking(input: string) {
    return 'Packing (Made-up)' === input || 'Packing (Fabric)' === input;
}

export function isChecking(input: string) {
    return 'Checking (Made-up)' === input || 'Checking (Fabric)' === input;
}

export function getSpecUrl(input: string, spec: any) {
    let url = null;

    if('Color Comp' === spec.fieldName) {
        url = GET_COL_COMP_URL;
    } else if('Material Comp' === spec.fieldName) {
        url = GET_MAT_COMP_URL;
    } else if('Certification' === spec.fieldName) {
        url = GET_CERT_URL;
    } else if(spec.fieldName.includes('Warp') || spec.fieldName.includes('Weft')) {
        url = GET_COUNT_URL;
    } else if('Color' === spec.fieldName) {
        url = GET_COLOR_URL;
    } else if('Product' === spec.fieldName || 'Item' === spec.fieldName) {
        url = GET_ALL_PRODUCT_URL;
    } else if('Quality' === spec.fieldName) {
         url = GET_QUALITY_URL;
    }

    return url;
}

export function getSpecList(specData: any) {
    if (specData.type === TYPE_MATERIAL) {
        return specData.input ? MATERIAL_SPECS_MAP[specData.input] : [];
    } else if (specData.type === TYPE_PROCESS) {
        return specData.input ? PROCESS_SPECS_MAP[specData.input] : [];
    } else if (specData.type === TYPE_LABOR) {
        if(specData.input) {
            if(isChecking(specData.input)) {
                return LABOR_SPECS_MAP['Checking'];
            } else if(isPacking(specData.input)) {
                return LABOR_SPECS_MAP['Packing'];
            } else {
                return LABOR_SPECS_MAP['Bale Packing'];
            }
        } else {
            return [];
        }
    }
}

export function getFontSize(text: any) {
    if (text.length > 45) {
      const scaleFactor = 45 / text.length;
      const calculatedSize = 9 * scaleFactor;
      return Math.max(calculatedSize);
    }
}

export type CustomGridColDef = GridColDef & {
    headerIcon?: React.ReactNode;
    headerIconText?: string;
    headerClickAction?: any;
    includeHeaderName?: boolean;
};
